import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import TestUpload from './pages/TestUpload';
import TestAnalysis from './pages/TestAnalysis';
import PracticeTestSetup from './pages/PracticeTestSetup';
import PracticeTestGenerating from './pages/PracticeTestGenerating';
import PracticeTestAttempt from './pages/PracticeTestAttempt';
import PracticeHistory from './pages/PracticeHistory';
import PracticeTestResult from './pages/PracticeTestResult';
import Teachers from './pages/Teachers';
import TeacherDetail from './pages/TeacherDetail';
import Payment from './pages/Payment';
import MyBookings from './pages/MyBookings';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import TeachersManagement from './pages/admin/TeachersManagement';
import BookingsManagement from './pages/admin/BookingsManagement';
import SettingsManagement from './pages/admin/SettingsManagement';
import SyllabusManagement from './pages/admin/SyllabusManagement';
import TeacherAvailabilityManagement from './pages/admin/TeacherAvailabilityManagement';
import StaffLayout from './components/StaffLayout';
import StaffDashboard from './pages/staff/StaffDashboard';
import StudentsList from './pages/staff/StudentsList';
import StudentDetail from './pages/staff/StudentDetail';
import UserManagement from './pages/staff/UserManagement';
import TicketsList from './pages/staff/TicketsList';
import TicketDetail from './pages/staff/TicketDetail';
import AnalyticsDashboard from './pages/staff/AnalyticsDashboard';
import FinanceReport from './pages/staff/FinanceReport';
import PaymentVerifications from './pages/staff/PaymentVerifications';
import ExpensesManagement from './pages/staff/ExpensesManagement';
import AuditLog from './pages/staff/AuditLog';
import SessionCoordination from './pages/staff/SessionCoordination';
import DiscoverSessions from './pages/DiscoverSessions';
import SessionInvite from './pages/SessionInvite';
import TeacherLayout from './components/TeacherLayout';
import TeacherHome from './pages/teacher/TeacherHome';
import TeacherHistory from './pages/teacher/TeacherHistory';
import TeacherAvailability from './pages/teacher/TeacherAvailability';
import TeacherPricing from './pages/teacher/TeacherPricing';
import TeacherRatings from './pages/teacher/TeacherRatings';
import TeacherTickets from './pages/teacher/TeacherTickets';

const STAFF_ROLES = ['director', 'admin', 'senior_executive', 'executive', 'finance'];
export const isStaffRole = (role) => STAFF_ROLES.includes(role);
const ROLE_FINANCE = ['director', 'finance'];
const ROLE_COORDINATION = ['director', 'admin', 'senior_executive', 'executive'];
const ROLE_ANALYTICS = ['director', 'admin', 'senior_executive'];
const ROLE_PLATFORM = ['director', 'admin'];
export const landingPathFor = (role) => (isStaffRole(role) ? '/staff' : role === 'teacher' ? '/teacher-portal' : '/');

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

let requestInterceptorId = null;
let responseInterceptorId = null;

export const setupAxiosInterceptors = (token, logout) => {
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
  }
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
  }

  requestInterceptorId = axios.interceptors.request.use(
    (config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const detail = error.response?.data?.detail || '';
        const lower = typeof detail === 'string' ? detail.toLowerCase() : '';
        if (lower.includes('blocked') || lower.includes('deactivated')) {
          logout();
          toast.error(detail);
        } else if (lower.includes('validate credentials') || lower.includes('token') || lower.includes('user not found')) {
          logout();
          toast.error('Session expired. Please login again.');
        } else {
          toast.error('Access denied for this action.');
        }
      }
      return Promise.reject(error);
    }
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const parseStoredUser = () => {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      localStorage.removeItem('user');
      return null;
    }
  };
  const storedUser = parseStoredUser();
  const resolvedUser = user || storedUser;

  useEffect(() => {
    const storedUser = parseStoredUser();
    if (token && storedUser) {
      setUser(storedUser);
      setupAxiosInterceptors(token, logout);
    }
    setLoading(false);
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
    setupAxiosInterceptors(authToken, logout);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user: resolvedUser, token, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/auth"
            element={!resolvedUser ? <AuthPage /> : <Navigate to={landingPathFor(resolvedUser?.role)} />}
          />

          {/* Staff Console (RBAC: director / senior_executive / executive / finance) */}
          <Route path="/staff" element={isStaffRole(resolvedUser?.role) ? <StaffLayout /> : <Navigate to="/auth" />}>
            <Route index element={<StaffDashboard />} />
            <Route path="students" element={<StudentsList />} />
            <Route path="students/:id" element={<StudentDetail />} />
            <Route path="sessions" element={ROLE_COORDINATION.includes(resolvedUser?.role) ? <SessionCoordination /> : <Navigate to="/staff" />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="tickets" element={<TicketsList />} />
            <Route path="tickets/:id" element={<TicketDetail />} />
            <Route path="analytics" element={ROLE_ANALYTICS.includes(resolvedUser?.role) ? <AnalyticsDashboard /> : <Navigate to="/staff" />} />
            <Route path="finance" element={ROLE_FINANCE.includes(resolvedUser?.role) ? <FinanceReport /> : <Navigate to="/staff" />} />
            <Route path="payments" element={ROLE_FINANCE.includes(resolvedUser?.role) ? <PaymentVerifications /> : <Navigate to="/staff" />} />
            <Route path="expenses" element={ROLE_FINANCE.includes(resolvedUser?.role) ? <ExpensesManagement /> : <Navigate to="/staff" />} />
            <Route path="audit" element={resolvedUser?.role === 'director' ? <AuditLog /> : <Navigate to="/staff" />} />
          </Route>

          {/* Platform Management (Director; Admin gets faculty management only) */}
          <Route path="/admin" element={ROLE_PLATFORM.includes(resolvedUser?.role) ? <AdminLayout /> : <Navigate to="/" />}>
            <Route index element={resolvedUser?.role === 'director' ? <AdminDashboard /> : <Navigate to="/admin/teachers" />} />
            <Route path="teachers" element={<TeachersManagement />} />
            <Route path="teachers/:teacherId/availability" element={<TeacherAvailabilityManagement />} />
            <Route path="bookings" element={resolvedUser?.role === 'director' ? <BookingsManagement /> : <Navigate to="/admin/teachers" />} />
            <Route path="settings" element={resolvedUser?.role === 'director' ? <SettingsManagement /> : <Navigate to="/admin/teachers" />} />
            <Route path="syllabus" element={resolvedUser?.role === 'director' ? <SyllabusManagement /> : <Navigate to="/admin/teachers" />} />
          </Route>

          {/* Teacher Portal */}
          <Route path="/teacher-portal" element={resolvedUser?.role === 'teacher' ? <TeacherLayout /> : <Navigate to={resolvedUser ? landingPathFor(resolvedUser.role) : '/auth'} />}>
            <Route index element={<TeacherHome />} />
            <Route path="history" element={<TeacherHistory />} />
            <Route path="availability" element={<TeacherAvailability />} />
            <Route path="pricing" element={<TeacherPricing />} />
            <Route path="ratings" element={<TeacherRatings />} />
            <Route path="support" element={<TeacherTickets />} />
          </Route>

          {/* Student Routes */}
          <Route
            path="/"
            element={
              resolvedUser
                ? resolvedUser.role === 'student' || !resolvedUser.role
                  ? <Layout />
                  : <Navigate to={landingPathFor(resolvedUser.role)} />
                : <Navigate to="/auth" />
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="upload" element={<TestUpload />} />
            <Route path="test/:testId" element={<TestAnalysis />} />
            <Route path="practice" element={<PracticeTestSetup />} />
            <Route path="practice/generating/:jobId" element={<PracticeTestGenerating />} />
            <Route path="practice/history" element={<PracticeHistory />} />
            <Route path="practice/results/:attemptId" element={<PracticeTestResult />} />
            <Route path="practice/:testId" element={<PracticeTestAttempt />} />
            <Route path="teachers" element={<Teachers />} />
            <Route path="teacher/:teacherId" element={<TeacherDetail />} />
            <Route path="payment" element={<Payment />} />
            <Route path="my-bookings" element={<MyBookings />} />
            <Route path="discover-sessions" element={<DiscoverSessions />} />
            <Route path="sessions/:bookingId" element={<SessionInvite />} />
          </Route>

          <Route path="*" element={<Navigate to={resolvedUser ? landingPathFor(resolvedUser.role) : '/auth'} replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;